"use client";

import { useState } from "react";
import { Frame, Slider, Verdict, Note } from "./parts";

/**
 * Le versement libératoire contre l'impôt au barème.
 *
 * La règle du glossaire veut qu'une démo démontre quelque chose que le texte
 * explique mal. Ici, c'est un point de bascule que presque personne ne connaît
 * et qui se calcule exactement.
 *
 * Le versement libératoire prélève un pourcentage du chiffre d'affaires, payé
 * quoi qu'il arrive. Le barème, lui, s'applique au chiffre d'affaires APRÈS
 * abattement, et vaut zéro si le foyer n'est pas imposable. Les deux se valent
 * donc quand :
 *
 *     taux du versement libératoire = (1 - abattement) x taux moyen d'imposition
 *
 * soit un taux moyen de 2,2 / 66 = 3,3 % en BNC, 1,7 / 50 = 3,4 % en services
 * BIC, 1 / 29 = 3,4 % en vente. Trois activités, trois abattements, trois taux
 * de versement, et un point de bascule quasi identique : c'est ce qui rend la
 * règle mémorisable, et c'est le résultat que la démo met sous les yeux.
 *
 * Aucun barème d'imposition n'est codé en dur : le lecteur donne son taux moyen
 * au curseur. C'est volontaire, le référentiel arbitré ne retenant que la limite
 * de la deuxième tranche, pas le barème complet.
 */

const L = {
    fr: {
        title: "À partir de quand le versement libératoire devient gagnant",
        activity: "Activité",
        vente: "Vente de marchandises",
        services: "Prestations de services (BIC)",
        bnc: "Activité libérale (BNC)",
        ca: "Chiffre d'affaires de l'année",
        rate: "Votre taux moyen d'imposition",
        withVfl: "Avec le versement libératoire",
        withScale: "Sans, imposition au barème",
        crossover: "Les deux se valent à",
        winVfl: "Le versement libératoire vous fait payer moins. L'écart se creuse à mesure que votre taux moyen monte.",
        winScale: "Le barème vous coûte moins cher ici. Le versement libératoire prélèverait plus que ce que vous devez.",
        winNothing: "Votre foyer n'est pas imposable : au barème, vous ne paieriez rien. Le versement libératoire, lui, prélève quand même. C'est le piège classique de l'option.",
        near: "Vous êtes au point de bascule : à ce niveau, les deux reviennent au même.",
        note: "Le taux moyen d'imposition est celui de votre foyer entier, pas votre tranche marginale : c'est l'impôt total divisé par le revenu imposable total. Il se lit sur votre avis d'imposition. Le calcul ci-dessus ne tient pas compte des autres revenus du foyer, qui décalent le résultat sans changer le point de bascule.",
        base: "Base imposable après abattement",
    },
    en: {
        title: "When the flat-rate income tax option starts paying off",
        activity: "Activity",
        vente: "Sale of goods",
        services: "Commercial services (BIC)",
        bnc: "Liberal profession (BNC)",
        ca: "Turnover for the year",
        rate: "Your household average tax rate",
        withVfl: "With the flat-rate option",
        withScale: "Without it, taxed on the scale",
        crossover: "The two are equal at",
        winVfl: "The flat-rate option costs you less, and the gap widens as your average rate rises.",
        winScale: "The standard scale is cheaper here. The flat-rate option would take more than you actually owe.",
        winNothing: "Your household is not taxable: under the scale you would pay nothing. The flat-rate option still takes its cut. This is the classic trap of the option.",
        near: "You are right at the tipping point: both come to the same thing here.",
        note: "The average tax rate is your household's, not your marginal bracket: total tax divided by total taxable income, as shown on your tax notice. The calculation ignores other household income, which shifts the amounts without moving the tipping point.",
        base: "Taxable base after allowance",
    },
};

// Abattements et taux de versement liberatoire 2026, referentiel arbite du
// 05/09/2026. Toute modification doit passer par ce referentiel.
const ACTIVITES = {
    vente: { abattement: 0.71, vfl: 0.01 },
    services: { abattement: 0.5, vfl: 0.017 },
    bnc: { abattement: 0.34, vfl: 0.022 },
};

const money = (v, lang) =>
    `${Math.round(v).toLocaleString(lang === "fr" ? "fr-FR" : "en-US")} €`;

export default function MeFlatRateTax({ lang = "fr" }) {
    const t = L[lang] || L.fr;
    const [activite, setActivite] = useState("bnc");
    const [ca, setCa] = useState(30000);
    const [tauxMoyen, setTauxMoyen] = useState(6);

    const { abattement, vfl } = ACTIVITES[activite];
    const base = ca * (1 - abattement);
    const impotVfl = ca * vfl;
    const impotBareme = base * (tauxMoyen / 100);

    // Le point de bascule ne depend NI du chiffre d'affaires NI du foyer :
    // c'est le rapport entre le taux de versement et la part imposable.
    const bascule = (vfl / (1 - abattement)) * 100;
    const ecart = impotBareme - impotVfl;
    const proche = Math.abs(tauxMoyen - bascule) < 0.35;

    const tone = tauxMoyen === 0 ? "bad" : proche ? "neutral" : ecart > 0 ? "good" : "warn";
    const verdict =
        tauxMoyen === 0
            ? t.winNothing
            : proche
              ? t.near
              : ecart > 0
                ? t.winVfl
                : t.winScale;

    return (
        <Frame icon="calculator-minimalistic-linear" title={t.title}>
            <div className="flex flex-wrap gap-2 mb-5">
                {["vente", "services", "bnc"].map((cle) => (
                    <button
                        key={cle}
                        type="button"
                        onClick={() => setActivite(cle)}
                        className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                            activite === cle
                                ? "bg-primary text-white"
                                : "bg-gray-100 dark:bg-white/[0.06] text-gray-600 dark:text-lightBlue hover:bg-gray-200 dark:hover:bg-white/10"
                        }`}
                    >
                        {t[cle]}
                    </button>
                ))}
            </div>

            <div className="space-y-4">
                <Slider
                    label={t.ca}
                    value={ca}
                    onChange={setCa}
                    min={5000}
                    max={80000}
                    step={1000}
                    display={money(ca, lang)}
                />
                <Slider
                    label={t.rate}
                    value={tauxMoyen}
                    onChange={setTauxMoyen}
                    min={0}
                    max={20}
                    step={0.5}
                    display={`${tauxMoyen.toString().replace(".", lang === "fr" ? "," : ".")} %`}
                />
            </div>

            <div className="grid grid-cols-2 gap-3 mt-5">
                <div
                    className={`rounded-xl p-4 border ${
                        ecart > 0 && tauxMoyen > 0
                            ? "border-primary/40 bg-primary/[0.06]"
                            : "border-gray-200/70 dark:border-white/10"
                    }`}
                >
                    <div className="text-xs text-gray-500 dark:text-lightBlue/70 mb-1">
                        {t.withVfl}
                    </div>
                    <div className="text-xl font-bold tabular-nums dark:text-white">
                        {money(impotVfl, lang)}
                    </div>
                </div>
                <div
                    className={`rounded-xl p-4 border ${
                        ecart <= 0 || tauxMoyen === 0
                            ? "border-primary/40 bg-primary/[0.06]"
                            : "border-gray-200/70 dark:border-white/10"
                    }`}
                >
                    <div className="text-xs text-gray-500 dark:text-lightBlue/70 mb-1">
                        {t.withScale}
                    </div>
                    <div className="text-xl font-bold tabular-nums dark:text-white">
                        {money(impotBareme, lang)}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-lightBlue/70 mt-1">
                        {t.base} : {money(base, lang)}
                    </div>
                </div>
            </div>

            <p className="text-sm text-gray-600 dark:text-lightBlue mb-0 mt-4">
                {t.crossover}{" "}
                <b className="dark:text-white">
                    {bascule.toFixed(1).replace(".", lang === "fr" ? "," : ".")} %
                </b>
            </p>

            <Verdict tone={tone}>{verdict}</Verdict>
            <Note>{t.note}</Note>
        </Frame>
    );
}
